import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth, useClerk } from '@clerk/clerk-react';
import { useGameStore } from '../store/gameStore';
import { gameAPI } from '../services/gameAPI';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { clearGenerationLifecycle } from '../utils/generationLifecycle';

const difficulties = [
  {
    id: 'easy',
    name: 'Casual Detective',
    suspects: '4-5 suspects',
    time: '~1 hour',
    description: 'Straightforward case with clear clues and helpful hints',
    color: 'green',
  },
  {
    id: 'medium',
    name: 'Private Eye',
    suspects: '6-7 suspects',
    time: '90-120 minutes',
    description: 'Balanced challenge with some red herrings',
    color: 'yellow',
  },
  {
    id: 'hard',
    name: 'Master Sleuth',
    suspects: '8-10 suspects',
    time: '2-3 hours',
    description: 'Complex case with multiple twists and limited guidance',
    color: 'red',
  },
];

const themeConfig = {
  noir: {
    name: 'Film Noir',
    accent: '#DC143C',
    gradient: 'from-slate-900 to-slate-800',
    bgImage: '/images/themes/noir-bg.jpg'
  },
  '90s_nyc': {
    name: '90s NYC',
    accent: '#FF6B35',
    gradient: 'from-orange-900 to-blue-900',
    bgImage: 'https://images.unsplash.com/photo-1496442226666-8d4d0e62e6e9?w=1920&h=1080&fit=crop'
  },
  miami_vice: {
    name: 'Miami Vice',
    accent: '#FF1493',
    gradient: 'from-purple-900 to-cyan-900',
    bgImage: '/images/themes/miami-bg.jpg'
  },
};

export const DifficultySelection = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // Came from the open Fresh stack on the home screen (ThemeSelection passes
  // this): on-screen Back returns there, matching browser back. Anything else
  // -- including a reload that lost the state -- goes to the plain home screen.
  const homePath = location.state?.homeStack === 'fresh' ? '/?stack=fresh' : '/';
  const { getToken } = useAuth();
  const { signOut } = useClerk();
  const { toast } = useToast();
  const { selectedTheme, setDifficulty, startGame } = useGameStore();
  const [selectedDiff, setSelectedDiff] = useState(null);
  const [isValidating, setIsValidating] = useState(false);
  const [quota, setQuota] = useState(null);
  const [quotaLoading, setQuotaLoading] = useState(true);

  useEffect(() => {
    const fetchQuota = async () => {
      try {
        const data = await gameAPI.getCaseGenQuota();
        setQuota(data);
      } catch (error) {
        console.error('Failed to fetch quota:', error);
      } finally {
        setQuotaLoading(false);
      }
    };
    fetchQuota();
  }, []);

  if (!selectedTheme) {
    navigate('/');
    return null;
  }

  const theme = themeConfig[selectedTheme];

  const handleStart = async () => {
    // Guard against concurrent validations
    if (!selectedDiff || isValidating) return;
    if (quota && !quota.unlimited && quota.remaining <= 0) return;

    setIsValidating(true);

    try {
      // Pre-flight auth check: validate token before starting case generation
      // This prevents orphaned cases when auth is stale
      const token = await getToken();

      if (!token) {
        // Token is expired/invalid - sign out and redirect
        toast({
          title: 'Session Expired',
          description: 'Please sign in again to start a new case.',
          variant: 'destructive'
        });
        try {
          await signOut();
        } catch (signOutErr) {
          console.warn('Error during sign out:', signOutErr);
        }
        navigate('/sign-in');
        return;
      }

      // Token is valid - proceed with case generation
      clearGenerationLifecycle();
      setDifficulty(selectedDiff);
      startGame();
      navigate('/game');
    } catch (error) {
      console.error('Auth validation failed:', error);
      toast({
        title: 'Session Error',
        description: 'Unable to verify your session. Please sign in again.',
        variant: 'destructive'
      });
      try {
        await signOut();
      } catch (signOutErr) {
        console.warn('Error during sign out:', signOutErr);
      }
      navigate('/sign-in');
    } finally {
      setIsValidating(false);
    }
  };

  return (
    <div 
      className={`min-h-screen flex flex-col items-center justify-center p-4 relative`}
      style={{
        backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)), url(${theme.bgImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      {/* Back Button */}
      <Button
        variant="ghost"
        className="absolute top-4 left-4 md:top-8 md:left-8 min-h-touch text-white hover:bg-white/10"
        onClick={() => navigate(homePath)}
      >
        <ArrowLeft className="mr-2 h-5 w-5" />
        <span className="hidden sm:inline">Back</span>
      </Button>

      {/* Progress Indicator - Hidden on mobile */}
      <div className="absolute top-4 right-4 md:top-8 md:right-8 hidden md:flex items-center gap-3 text-white">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: theme.accent }}></div>
          <span className="text-sm">Theme Selected</span>
        </div>
        <div className="w-8 h-0.5 bg-white/30"></div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-white"></div>
          <span className="text-sm font-semibold">Difficulty</span>
        </div>
        <div className="w-8 h-0.5 bg-white/30"></div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-white/30"></div>
          <span className="text-sm text-white/50">Game Start</span>
        </div>
      </div>

      {/* Title */}
      <div className="text-center mb-8 md:mb-12 space-y-2 pt-16 md:pt-0">
        <div className="text-lg md:text-2xl uppercase tracking-widest font-bold" style={{ color: theme.accent }}>
          {theme.name}
        </div>
        <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white"
            style={{ fontFamily: 'Playfair Display, serif' }}>
          Choose Your Challenge
        </h1>
      </div>

      {/* Daily Case Generation Quota */}
      {!quotaLoading && quota && !quota.unlimited && (
        <div className={`mb-6 rounded-lg border p-4 max-w-5xl w-full px-2 md:px-4 ${
          quota.remaining > 0
            ? 'bg-gray-800/50 border-gray-700'
            : 'bg-red-900/30 border-red-700'
        }`}>
          <div className="flex items-center gap-2">
            {quota.remaining > 0 ? (
              <>
                <div className="text-sm text-gray-300">
                  <span className="font-semibold text-white">{quota.remaining}</span> case{quota.remaining !== 1 ? 's' : ''} remaining today
                  <span className="text-gray-500 ml-2">({quota.used_today}/{quota.daily_limit} used)</span>
                </div>
              </>
            ) : (
              <div className="text-sm">
                <p className="text-red-300 font-medium">
                  You've reached your daily limit of {quota.daily_limit} case{quota.daily_limit !== 1 ? 's' : ''}.
                </p>
                <p className="text-gray-400 mt-1">
                  Your limit resets at midnight ET. Need more?{' '}
                  <a href="mailto:support@deandrae.com" className="text-blue-400 hover:text-blue-300 underline">
                    Email support@deandrae.com
                  </a>
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Difficulty Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 max-w-5xl w-full mb-6 md:mb-8 px-2 md:px-0">
        {difficulties.map((diff) => (
          <Card
            key={diff.id}
            className={`p-4 md:p-6 cursor-pointer transition-all duration-300 border-2 min-h-touch ${
              selectedDiff === diff.id
                ? `scale-[1.02] md:scale-105 shadow-2xl border-white`
                : 'scale-100 shadow-lg border-white/20 hover:border-white/40'
            } bg-black/40 backdrop-blur-sm`}
            style={{
              boxShadow: selectedDiff === diff.id ? `0 10px 40px ${theme.accent}40` : undefined,
            }}
            onClick={() => setSelectedDiff(diff.id)}
          >
            <div className="space-y-3 md:space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-xl md:text-2xl font-bold text-white">{diff.name}</h3>
                {selectedDiff === diff.id && (
                  <div className="w-6 h-6 rounded-full flex items-center justify-center" style={{ backgroundColor: theme.accent }}>
                    <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                )}
              </div>

              <div className="space-y-1 md:space-y-2 text-gray-300">
                <div className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: theme.accent }}></div>
                  <span className="text-sm">{diff.suspects}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: theme.accent }}></div>
                  <span className="text-sm">{diff.time}</span>
                </div>
              </div>

              <p className="text-gray-400 text-sm leading-relaxed">{diff.description}</p>
            </div>
          </Card>
        ))}
      </div>

      {/* Start Button */}
      <Button
        size="lg"
        disabled={!selectedDiff || isValidating || (quota && !quota.unlimited && quota.remaining <= 0)}
        onClick={handleStart}
        className="min-h-touch px-6 md:px-8 py-4 md:py-6 text-base md:text-lg font-semibold uppercase tracking-wider disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300"
        style={{
          backgroundColor: selectedDiff && !isValidating ? theme.accent : '#666',
          color: 'white',
        }}
      >
        {isValidating ? (
          <>
            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
            Verifying...
          </>
        ) : (
          'Start Investigation'
        )}
      </Button>
    </div>
  );
};
